import React, { useEffect } from "react";
import { Link } from "react-router-dom";

// Shared layout for legal documents — consistent typography, spacing, and testids.
function LegalLayout({ title, effective, updated, children, testid }) {
  useEffect(() => { document.title = `${title} · Accommodation Suppliers`; }, [title]);
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid={testid}>
      <div className="tracking-overline mb-3 text-[#B35C4A]">QuArete Pty Ltd, trading as Accommodation Suppliers</div>
      <h1 className="font-serif text-4xl md:text-5xl text-[#11211A] tracking-tight mb-3">{title}</h1>
      <div className="text-xs uppercase tracking-widest text-[#7D8C85] mb-10 flex gap-4 flex-wrap">
        {effective && <span>Effective: {effective}</span>}
        {updated && <span>Last Updated: {updated}</span>}
      </div>
      <article className="prose prose-neutral max-w-none text-[#22332C] leading-relaxed">
        {children}
      </article>
      <div className="mt-16 pt-8 border-t border-[#E8ECE9] text-xs text-[#7D8C85] flex flex-wrap gap-6">
        <Link to="/terms" className="hover:text-[#264839]" data-testid="legal-nav-terms">Terms of Use</Link>
        <Link to="/privacy" className="hover:text-[#264839]" data-testid="legal-nav-privacy">Privacy Policy</Link>
        <Link to="/payments-refunds" className="hover:text-[#264839]" data-testid="legal-nav-payments">Payments, Refunds &amp; Disclaimers</Link>
      </div>
    </div>
  );
}

// --- Reusable rich-text primitives with brand tokens ---
const H2 = ({ children }) => (
  <h2 className="font-serif text-2xl text-[#11211A] mt-10 mb-4 tracking-tight">{children}</h2>
);
const H3 = ({ children }) => (
  <h3 className="font-semibold text-[15px] uppercase tracking-widest text-[#264839] mt-6 mb-2">{children}</h3>
);
const P = ({ children }) => <p className="text-[15px] my-3 leading-[1.75]">{children}</p>;
const UL = ({ children }) => <ul className="list-disc pl-6 my-3 space-y-1.5 text-[15px] leading-[1.7]">{children}</ul>;
const B = ({ children }) => <strong className="font-semibold text-[#11211A]">{children}</strong>;

// =============================================================================
// TERMS OF USE
// =============================================================================
export function TermsPage() {
  return (
    <LegalLayout title="Terms of Use" effective="October 6, 2025" updated="February 14, 2026" testid="terms-page">
      <H2>1. Acceptance of Terms</H2>
      <P>By accessing or using www.accommodationsuppliers.com (“Website”), you agree to be bound by these Terms of Use (“Terms”). If you do not agree, please do not use our Website.</P>
      <P>We may update these Terms from time to time. The latest version will always be available on this page, with the date of last update shown above.</P>

      <H2>2. About Us</H2>
      <P>The Website is operated by QuArete Pty Ltd, trading as Accommodation Suppliers (“we”, “our”, “us”), a company registered in Australia.</P>
      <P>Our Website is a global online directory that connects consumers with accommodation providers and travel-related businesses and services.</P>

      <H2>3. General Use of the Website</H2>
      <P>You agree to use the Website only for lawful purposes and in accordance with these Terms. You must not:</P>
      <UL>
        <li>Use the Website in any way that breaches applicable laws or regulations</li>
        <li>Post or submit false, misleading, or unauthorised content</li>
        <li>Attempt to interfere with the operation, security, or integrity of the Website</li>
        <li>Use the Website to transmit harmful code (e.g., viruses, malware)</li>
        <li>Infringe intellectual property rights or breach any laws</li>
      </UL>

      <H2>4. Consumer Use</H2>
      <H3>Browsing &amp; Search</H3>
      <P>Consumers may browse listings free of charge. We do not endorse or guarantee any business, product, or service listed on the Website.</P>
      <H3>No Professional Advice</H3>
      <P>Content on the Website, including listings, articles, or other materials, is not intended to be professional advice (legal, medical, financial, or otherwise). You should seek independent professional advice before relying on any information found on this Website.</P>
      <H3>Direct Arrangements</H3>
      <P>Any engagement between consumers and advertisers is a direct arrangement between those parties. We are not a travel agent, booking service, or party to any contract formed between consumers and advertisers.</P>
      <H3>Consumer Responsibility</H3>
      <P>We are not responsible for the quality, safety, suitability, or legality of any services provided by listed businesses. Accessing services through listed businesses is at your own risk.</P>

      <H2>5. Advertiser Terms</H2>
      <H3>Application</H3>
      <P>This section applies to all businesses (“Advertiser”, “you”, “your”) who create listings, purchase advertising services, or otherwise promote their business on the Website.</P>
      <P>By submitting a listing or purchasing advertising, you agree to these Terms in addition to our Privacy Policy and Payments, Refunds &amp; Disclaimers Policy.</P>
      <H3>Advertiser Responsibilities</H3>
      <P>You agree that:</P>
      <UL>
        <li>All information you provide is accurate, lawful, and not misleading</li>
        <li>You have the authority to represent the business being advertised</li>
        <li>You will promptly update your listing if details change</li>
        <li>Your content does not infringe intellectual property rights or breach any laws</li>
        <li>You will not post offensive, defamatory, or inappropriate material</li>
      </UL>
      <H3>Our Role as Directory Platform</H3>
      <P>We operate as a directory platform only. We do not:</P>
      <UL>
        <li>Endorse, guarantee, or verify any advertiser, business, product, or service</li>
        <li>Accept responsibility for contracts, payments, or arrangements made between advertisers and consumers</li>
        <li>Guarantee the number of views, enquiries, or conversions your listing may generate</li>
      </UL>
      <H3>Listings &amp; Content Management</H3>
      <P>We may edit, refuse, suspend, or remove listings that:</P>
      <UL>
        <li>Breach this Agreement or our content guidelines, or</li>
        <li>Could harm our reputation or the experience of Website users</li>
      </UL>
      <P>You retain ownership of your submitted content but grant us a non-exclusive, royalty-free, worldwide licence to display and promote it on our Website and related media.</P>
      <H3>Fees &amp; Payments</H3>
      <P>Advertisers may be required to pay fees for premium listings, placements, or advertising services.</P>
      <UL>
        <li>Fees will be clearly stated at the point of purchase</li>
        <li>Our listing fees originate in USD and are converted into your local currency at the time of purchase</li>
        <li>You will be invoiced in your local currency</li>
        <li>All fees are payable in advance through approved payment providers</li>
        <li>Payments are processed securely via third-party payment providers</li>
        <li>Fees are subject to our Payments, Refunds &amp; Disclaimers Policy</li>
      </UL>
      <H3>Termination of Advertiser Listings</H3>
      <P>You may remove your listing or cancel advertising at any time. We may suspend or terminate your listing without notice if you breach these Terms.</P>

      <H2>6. Intellectual Property</H2>
      <P>All content on the Website, including text, graphics, logos, and design, is owned by or licensed to us and is protected by copyright, trademark, and other intellectual property laws.</P>
      <P>Advertisers retain ownership of their submitted content but grant us a non-exclusive, royalty-free, worldwide licence to display and promote that content on the Website.</P>
      <P>You must not reproduce, distribute, or create derivative works from our Website content without our prior written consent.</P>

      <H2>7. Third-Party Links</H2>
      <P>Our Website may contain links to third-party websites. We are not responsible for the content, accuracy, reliability, or practices of any third-party site. Accessing such sites is at your own risk.</P>

      <H2>8. Disclaimer &amp; Limitation of Liability</H2>
      <H3>General Disclaimer</H3>
      <P>The Website and its content are provided “as is” without warranties of any kind, express or implied.</P>
      <P>We do not guarantee that the Website will be:</P>
      <UL>
        <li>Uninterrupted, secure, or error-free</li>
        <li>Complete, accurate, or up-to-date</li>
        <li>Suitable for your particular purpose</li>
      </UL>
      <H3>Limitation of Liability</H3>
      <P>To the fullest extent permitted by law, QuArete Pty Ltd, trading as Accommodation Suppliers, shall not be liable for any loss, damage, or claim arising from:</P>
      <UL>
        <li>Use of the Website or reliance on its content</li>
        <li>Dealings between consumers and advertisers</li>
        <li>Errors, omissions, interruptions, or delays in the Website’s operation</li>
        <li>Any breach of these Terms by third parties</li>
      </UL>
      <H3>Consumer Rights Protection</H3>
      <P>Nothing in these Terms excludes liability where it cannot be excluded under applicable law (e.g., Australian Consumer Law, EU consumer rights, or other mandatory local consumer protections).</P>

      <H2>9. Indemnity</H2>
      <P>You agree to indemnify and hold us harmless against any claims, losses, or expenses arising out of your breach of these Terms or misuse of the Website.</P>

      <H2>10. Termination</H2>
      <P>We may suspend or terminate your access to the Website at any time, without notice, if you breach these Terms or use the Website in a manner that could harm us or others.</P>

      <H2>11. Governing Law</H2>
      <P>These Terms are governed by the laws of Australia.</P>
      <P>If you are a consumer in the EU, UK, New Zealand, or another jurisdiction with mandatory local consumer protections, you may have additional rights under your local laws.</P>

      <H2>12. Contact Us</H2>
      <P>If you have questions about these Terms, please contact us via:</P>
      <UL>
        <li><B>Email:</B> hello@accommodationsuppliers.com</li>
        <li><B>Online Contact Form:</B> www.accommodationsuppliers.com/contact</li>
        <li><B>Postal Address:</B> Available on request</li>
      </UL>
    </LegalLayout>
  );
}

// =============================================================================
// PAYMENTS, REFUNDS & DISCLAIMERS
// =============================================================================
export function PaymentsRefundsPage() {
  return (
    <LegalLayout title="Payments, Refunds & Disclaimers" effective="October 1, 2025" updated="February 14, 2026" testid="payments-refunds-page">
      <H2>1. Scope</H2>
      <P>This Payments, Refunds &amp; Disclaimers Policy applies to all advertisers and businesses (“you”, “your”) who purchase paid listings, premium placements, or advertising services (“Services”) on www.accommodationsuppliers.com (“Website”).</P>
      <P>By purchasing Services from us, you agree to this Policy in addition to our Terms of Use and Privacy Policy.</P>

      <H2>2. Free Listings</H2>
      <P>Basic directory listings are free of charge.</P>
      <P>This Policy applies only to paid listings, premium placements, and advertising services.</P>

      <H2>3. Accepted Payment Methods</H2>
      <P>We accept payments via secure third-party payment providers, including credit/debit cards, PayPal, and other approved methods (subject to availability in your country).</P>
      <P>We do not store your full payment details. All transactions are processed securely through our payment providers.</P>

      <H2>4. Billing &amp; Invoicing</H2>
      <P>All fees originate in USD and are converted to your country of purchase currency.</P>
      <P>You are responsible for any applicable currency conversion fees or bank charges.</P>
      <P>Upon successful payment, you will receive an electronic invoice/receipt via email.</P>

      <H2>5. Subscriptions &amp; Renewals</H2>
      <P>Paid listings and advertising services may be offered on a subscription basis (e.g., monthly or annually).</P>
      <P>Unless otherwise stated, subscriptions will automatically renew at the end of each billing cycle.</P>
      <P>You may cancel auto-renewal at any time through your account settings or by contacting us (see Section 13).</P>

      <H2>6. Taxes</H2>
      <P>Fees may be subject to Goods and Services Tax (GST), Value Added Tax (VAT), sales tax, or similar local taxes, depending on your location.</P>
      <P>Where applicable, taxes will be clearly displayed at checkout.</P>
      <P>You are responsible for any additional taxes or duties imposed in your jurisdiction.</P>

      <H2>7. Cancellations</H2>
      <P>You may cancel your subscription or advertising service at any time by logging into your account or contacting us.</P>
      <P>Cancellations will take effect at the end of the current billing cycle.</P>
      <P>No further payments will be taken after cancellation.</P>

      <H2>8. Refunds</H2>
      <P><B>General Rule:</B> Fees are non-refundable once paid, except as required by law.</P>
      <P><B>Accidental Renewals:</B> If your subscription renews unintentionally, you must contact us within 7 days to request a refund.</P>
      <P><B>Service Failures:</B> If we are unable to provide the service you purchased due to a technical fault on our part (not related to your system, devices, or internet connection), you may request a partial or full refund.</P>
      <P><B>Pro-Rata Refunds:</B> If we significantly change or discontinue a service you have already paid for, we may offer a pro-rata refund for the unused portion.</P>
      <P><B>Legal Rights:</B> Nothing in this Policy affects your statutory rights under applicable consumer protection laws in your jurisdiction.</P>

      <H2>9. Late or Failed Payments</H2>
      <P>If a payment fails or is reversed, we may suspend or terminate your access to paid Services until payment is received.</P>
      <P>You remain responsible for all outstanding amounts.</P>

      <H2>10. Pricing Changes</H2>
      <P>We may update our pricing from time to time.</P>
      <P>Existing subscribers will be notified at least 30 days in advance of any price change.</P>
      <P>If you do not agree with the new pricing, you may cancel before the change takes effect.</P>

      <H2>11. General Disclaimers</H2>
      <H3>Information Purposes Only</H3>
      <P>The information provided on the Website is for general informational purposes only. While we strive to ensure accuracy and reliability, we make no guarantees about the completeness, accuracy, or timeliness of any content.</P>
      <H3>No Professional Advice</H3>
      <P>Content on the Website, including listings, articles, or other materials, is not intended to be professional advice (legal, medical, financial, or otherwise). You should seek independent professional advice before relying on any information found on this Website.</P>
      <H3>Directory Nature</H3>
      <P>The Website is an online directory. We do not endorse, recommend, or verify the businesses, products, or services listed.</P>
      <H3>External Links</H3>
      <P>The Website may contain links to external websites. We are not responsible for the content, accuracy, or reliability of any third-party sites. Accessing external links is at your own risk.</P>

      <H2>12. Limitation of Liability</H2>
      <P>To the fullest extent permitted by law, QuArete Pty Ltd, trading as Accommodation Suppliers, shall not be liable for any loss, damage, or claim arising from:</P>
      <UL>
        <li>Use of the Website or reliance on its content</li>
        <li>Dealings between consumers and advertisers</li>
        <li>Errors, omissions, interruptions, or delays in the Website’s operation</li>
        <li>Payment processing errors or delays caused by third-party payment providers</li>
        <li>Currency conversion fluctuations</li>
        <li>Tax obligations in your jurisdiction</li>
      </UL>
      <H3>Consumer Rights Protection</H3>
      <P>Nothing in this Policy excludes liability where it cannot be excluded under applicable law (e.g., Australian Consumer Law, EU consumer rights, or other mandatory local consumer protections).</P>

      <H2>13. Refunds Process</H2>
      <P>To request a refund or discuss a cancellation, please contact us via:</P>
      <UL>
        <li><B>Email:</B> hello@accommodationsuppliers.com</li>
        <li><B>Online Contact Form:</B> www.accommodationsuppliers.com/contact</li>
      </UL>
      <P>Refunds, if approved, will be processed via the same payment method used for purchase within 10–14 business days.</P>

      <H2>14. Governing Law</H2>
      <P>This Policy is governed by the laws of Australia.</P>
      <P>If you are located elsewhere, you may also have rights under your local consumer protection and e-commerce laws.</P>

      <H2>15. Changes to This Policy</H2>
      <P>We may update this Policy from time to time. Updates will be posted on this page with the revised “Last Updated” date.</P>
    </LegalLayout>
  );
}

// =============================================================================
// PRIVACY POLICY
// =============================================================================
export function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy" effective="October 1, 2025" updated="February 14, 2026" testid="privacy-page">
      <H2>1. Introduction</H2>
      <P>QuArete Pty Ltd, trading as Accommodation Suppliers (“we”, “our”, “us”) respects your privacy and is committed to protecting your personal information.</P>
      <P>This Privacy Policy explains how we collect, use, disclose, and safeguard personal information when you use our website www.accommodationsuppliers.com (“Website”) and related services.</P>
      <P>We are based in Australia, but our services are offered globally. Accordingly, this Privacy Policy is designed to comply with:</P>
      <UL>
        <li>Privacy Act 2020 (New Zealand)</li>
        <li>Privacy Act 1988 (Australia) and the Australian Privacy Principles (APPs)</li>
        <li>General Data Protection Regulation (GDPR) (EU/UK)</li>
        <li>California Consumer Privacy Act (CCPA) &amp; California Privacy Rights Act (CPRA)</li>
        <li>Other applicable global data protection and ePrivacy regulations</li>
      </UL>
      <P>You may contact us via email or through our online contact form (see Section 13). Our postal address is available on request if required.</P>
      <P>By using our Website, you agree to the practices described in this Privacy Policy.</P>

      <H2>2. Information We Collect</H2>
      <P>We may collect the following categories of personal information:</P>
      <UL>
        <li><B>Contact Information:</B> Name, email address, phone number</li>
        <li><B>Account Information:</B> Username, password, advertiser profiles</li>
        <li><B>Payment Information:</B> Billing address, payment details (processed via secure third-party providers)</li>
        <li><B>Business Information:</B> Business name, services, location, categories selected</li>
        <li><B>Technical Information:</B> IP address, browser type, device information, usage statistics</li>
        <li><B>Cookies &amp; Tracking Data:</B> Behavioural data collected through cookies, analytics, and tracking technologies</li>
      </UL>

      <H2>3. How We Collect Information</H2>
      <UL>
        <li>Directly from you when you register, submit a listing, or contact us</li>
        <li>Automatically through cookies and analytics when you use our Website</li>
        <li>From third-party service providers (e.g., payment processors, analytics tools)</li>
      </UL>

      <H2>4. How We Use Information</H2>
      <P>We use personal information to:</P>
      <UL>
        <li>Provide and improve our services</li>
        <li>Manage advertiser accounts and process payments</li>
        <li>Respond to enquiries and provide customer support</li>
        <li>Send service updates, newsletters, and promotional communications (where consented)</li>
        <li>Analyse website usage and improve functionality</li>
        <li>Detect and prevent fraud or misuse of our services</li>
        <li>Comply with legal obligations</li>
      </UL>

      <H2>5. Legal Basis for Processing (GDPR/EU &amp; UK Users)</H2>
      <P>If you are located in the European Union or United Kingdom, we rely on the following legal bases for processing:</P>
      <UL>
        <li><B>Consent</B> — where you agree to receive marketing or accept cookies</li>
        <li><B>Contract</B> — where processing is necessary to provide our services</li>
        <li><B>Legal Obligation</B> — where we must retain data for tax or regulatory purposes</li>
        <li><B>Legitimate Interests</B> — such as fraud prevention, site improvement, and customer support</li>
      </UL>

      <H2>6. Disclosure of Information</H2>
      <P>We may share information with:</P>
      <UL>
        <li><B>Service Providers:</B> Hosting, payment processing, email, and analytics providers</li>
        <li><B>Legal Authorities:</B> Where required by law or to protect our rights</li>
        <li><B>International Transfers:</B> Your information may be transferred outside your country, including to jurisdictions with different data protection laws. We take reasonable steps to ensure recipients provide adequate safeguards (e.g., contractual clauses, trusted providers).</li>
      </UL>
      <P>We do not sell personal information to third parties.</P>

      <H2>7. Cookies &amp; Tracking Technologies</H2>
      <P>We use cookies and similar technologies to:</P>
      <UL>
        <li>Enable website functionality</li>
        <li>Analyse traffic and performance</li>
        <li>Personalise user experience</li>
        <li>Deliver relevant advertising</li>
      </UL>
      <P>By using our Website, you consent to our use of cookies in accordance with applicable laws (including EU ePrivacy Directive and GDPR). You may manage or disable cookies in your browser settings.</P>
      <P>For EU/UK visitors, we display a cookie consent banner to obtain your preferences.</P>

      <H2>8. Your Rights</H2>
      <P>Depending on where you live, you may have the following rights:</P>
      <H3>New Zealand &amp; Australia</H3>
      <UL>
        <li>Access and correction of your personal information</li>
      </UL>
      <H3>EU/UK (GDPR)</H3>
      <UL>
        <li>Access, correction, deletion, restriction of processing, portability, objection, and withdrawal of consent</li>
      </UL>
      <H3>California (CCPA/CPRA)</H3>
      <UL>
        <li>Right to know what personal information we collect, use, and disclose</li>
        <li>Right to delete personal information</li>
        <li>Right to opt-out of the “sale” or “sharing” of personal information</li>
        <li>Right to non-discrimination for exercising your privacy rights</li>
      </UL>
      <P>To exercise your rights, please contact us (see Section 13).</P>

      <H2>9. Data Retention</H2>
      <P>We retain personal information only for as long as necessary to fulfil the purposes outlined in this Policy or to comply with legal and contractual obligations.</P>

      <H2>10. Security of Information</H2>
      <P>We use appropriate technical and organisational measures to protect personal information, including encryption, secure servers, and restricted access.</P>
      <P>However, no method of transmission over the Internet is completely secure, and we cannot guarantee absolute security.</P>

      <H2>11. Children’s Privacy</H2>
      <P>Our Website is not directed at children under 16 years of age. We do not knowingly collect personal information from children. If you believe we have inadvertently collected information about a child, please contact us immediately.</P>

      <H2>12. Changes to This Policy</H2>
      <P>We may update this Privacy Policy from time to time. Any changes will be posted on this page with the updated date. Significant changes will be communicated via email or a notice on our Website.</P>

      <H2>13. Contact Us</H2>
      <P>If you have any questions or concerns about this Privacy Policy or how your personal information is handled, you can contact us through either of the following:</P>
      <UL>
        <li><B>Email:</B> hello@accommodationsuppliers.com</li>
        <li><B>Online Contact Form:</B> www.accommodationsuppliers.com/contact</li>
        <li><B>Postal Address:</B> Available on request</li>
      </UL>
      <P>If you are not satisfied with our response, you may lodge a complaint with:</P>
      <UL>
        <li><B>New Zealand:</B> Office of the Privacy Commissioner — www.privacy.org.nz</li>
        <li><B>Australia:</B> Office of the Australian Information Commissioner — www.oaic.gov.au</li>
        <li><B>EU/UK:</B> Your local data protection authority</li>
        <li><B>California:</B> California Privacy Protection Agency</li>
      </UL>
    </LegalLayout>
  );
}
